import React, { useEffect } from "react";
import { Link, useLocation } from "react-router-dom";
import { assets, onAssetError } from "./assets";
import {
  CATEGORIES,
  FAQS,
  INTEGRATIONS,
  MARQUEE_BRANDS,
  PRODUCTS,
  SERVICES,
  STAGE_LABELS,
  STEPS,
  SUPPLY_STAGES,
  TESTIMONIALS,
} from "./data";
import { ArrowRight, ArrowUpRight, Check, Icon, Play, iconPaths } from "./icons";
import { useLanding } from "./LandingContext";

export function HomePage() {
  const location = useLocation();
  const { setMode } = useLanding();

  useEffect(() => {
    const hash = (location.hash || "").replace("#", "");
    if (hash === "products") setMode("products");
    else if (hash === "services" || hash === "how") setMode("fulfillment");
    if (hash) {
      const t = setTimeout(() => {
        const el = document.getElementById(hash);
        if (el) el.scrollIntoView({ behavior: "auto" });
      }, 80);
      return () => clearTimeout(t);
    }
  }, [location.hash, setMode]);

  return (
    <>
      <Hero />
      <Proof />
      <ServicesSplit />
      <SupplyChain />
      <ProductsBlock />
      <HowItWorks />
      <Categories />
      <Integrations />
      <Testimonials />
      <FAQ />
      <FinalCta />
    </>
  );
}

function Hero() {
  const { mode, setMode, goTo } = useLanding();
  const sw = mode === "products";
  const hero = sw
    ? {
        heroTitleA: "Your operations,",
        heroTitleB: "powered by our software.",
        heroSub:
          "EcomHQ, Backtracking, POS Bazaars and self-fulfillment — the systems that run Chainz, coming soon for brands operating their own warehouse and team.",
        heroCtaLabel: "Get in touch",
        heroCtaHref: "/contact",
        heroGhostLabel: "See warehousing",
        heroGhostHref: "#hero",
        onCta: goTo("contact-page", null, "contact"),
        onGhost: (e) => {
          if (e) e.preventDefault();
          setMode("fulfillment");
        },
      }
    : {
        heroTitleA: "You handle the business,",
        heroTitleB: "we handle the operations.",
        heroSub:
          "Storage, picking, packing, shipping and cash collection — one partner for your entire fulfillment chain, live in one dashboard.",
        heroCtaLabel: "Become a Partner",
        heroCtaHref: "/book?intent=partnership",
        heroGhostLabel: "Book a Demo",
        heroGhostHref: "/book?intent=demo",
        onCta: undefined,
        onGhost: undefined,
      };

  return (
    <div id="hero" style={{ padding: 0 }} data-screen-label="Hero">
      <div
        style={{
          width: "100%",
          margin: 0,
          position: "relative",
          borderRadius: "0 0 40px 40px",
          overflow: "hidden",
          minHeight: "100vh",
          marginTop: -84,
        }}
      >
        <div style={{ position: "absolute", inset: 0, pointerEvents: "none" }}>
          <img
            src={assets.hero}
            alt=""
            onError={onAssetError}
            style={{ width: "100%", height: "100%", objectFit: "cover", display: "block" }}
          />
        </div>
        <div
          style={{
            position: "absolute",
            inset: 0,
            background:
              "linear-gradient(180deg, rgba(12,6,7,0.62) 0%, rgba(12,6,7,0.42) 34%, rgba(12,6,7,0.5) 60%, rgba(12,6,7,0.82) 100%)",
            pointerEvents: "none",
          }}
        />
        <div
          style={{
            position: "absolute",
            inset: 0,
            background: "radial-gradient(circle at 42% 44%, rgba(0,0,0,0.55), transparent 60%)",
            pointerEvents: "none",
          }}
        />

        <div
          className="rv in"
          style={{
            position: "relative",
            zIndex: 2,
            padding: "120px clamp(18px, 5vw, 44px) 40px",
            display: "flex",
            flexDirection: "column",
            alignItems: "center",
            minHeight: "92vh",
            boxSizing: "border-box",
            pointerEvents: "none",
          }}
        >
          <div
            className="rvc"
            style={{
              pointerEvents: "auto",
              display: "inline-flex",
              gap: 5,
              padding: 5,
              borderRadius: 999,
              background: "linear-gradient(150deg, rgba(255,255,255,0.20), rgba(255,255,255,0.06))",
              border: "1px solid rgba(255,255,255,0.34)",
              boxShadow: "inset 0 1px 0 rgba(255,255,255,0.4), 0 12px 30px rgba(0,0,0,0.22)",
              marginBottom: 24,
            }}
          >
            <button type="button" className={`seg${!sw ? " on" : ""}`} onClick={() => setMode("fulfillment")}>
              Warehousing
            </button>
            <button type="button" className={`seg${sw ? " on" : ""}`} onClick={() => setMode("products")}>
              Solutions
            </button>
          </div>

          <div
            className="rvc"
            style={{
              fontSize: "clamp(38px, 8.5vw, 66px)",
              fontWeight: 300,
              lineHeight: 1.05,
              letterSpacing: "-0.02em",
              textAlign: "center",
              color: "#fff",
              marginTop: 22,
              maxWidth: 940,
              textWrap: "balance",
              textShadow: "0 4px 24px rgba(0,0,0,0.65), 0 14px 60px rgba(0,0,0,0.6)",
            }}
          >
            {hero.heroTitleA}
            <br />
            <span style={{ fontWeight: 800 }}>{hero.heroTitleB}</span>
          </div>
          <div
            className="rvc"
            style={{
              fontSize: 18,
              lineHeight: 1.6,
              color: "rgba(255,255,255,0.95)",
              marginTop: 22,
              maxWidth: 600,
              textAlign: "center",
              textWrap: "pretty",
              textShadow: "0 2px 16px rgba(0,0,0,0.6)",
            }}
          >
            {hero.heroSub}
          </div>
          <div className="rvc" style={{ display: "flex", gap: 14, marginTop: 30, pointerEvents: "auto", flexWrap: "wrap", justifyContent: "center" }}>
            {hero.heroCtaHref.startsWith("/") ? (
              <Link className="btn red" to={hero.heroCtaHref} style={{ padding: "15px 28px", fontSize: 15 }}>
                {hero.heroCtaLabel}
                <ArrowRight />
              </Link>
            ) : (
              <a className="btn red" href={hero.heroCtaHref} onClick={hero.onCta} style={{ padding: "15px 28px", fontSize: 15 }}>
                {hero.heroCtaLabel}
                <ArrowRight />
              </a>
            )}
            {hero.heroGhostHref.startsWith("/") ? (
              <Link className="btn ghost-lite" to={hero.heroGhostHref} style={{ padding: "15px 28px", fontSize: 15 }}>
                {hero.heroGhostLabel}
              </Link>
            ) : (
              <a className="btn ghost-lite" href={hero.heroGhostHref} onClick={hero.onGhost} style={{ padding: "15px 28px", fontSize: 15 }}>
                {hero.heroGhostLabel}
              </a>
            )}
          </div>

          <div className="hero-proof-text rvc" style={{ alignItems: "center", gap: 11, marginTop: 24, pointerEvents: "auto" }}>
            <AvatarStack size={30} />
            <span style={{ fontSize: 14, fontWeight: 500, color: "rgba(255,255,255,0.92)", textShadow: "0 2px 12px rgba(0,0,0,0.6)" }}>
              <span style={{ fontWeight: 800, color: "#fff" }}>50+ brands</span> already building with Chainz
            </span>
          </div>

          <div
            style={{
              marginTop: "auto",
              width: "100%",
              display: "flex",
              alignItems: "flex-end",
              justifyContent: "space-between",
              gap: 24,
              pointerEvents: "auto",
              flexWrap: "wrap",
            }}
          >
            <div
              className="rvc hero-joined"
              style={{
                maxWidth: 366,
                padding: "26px 28px",
                borderRadius: 26,
                background: "linear-gradient(150deg, rgba(255,255,255,0.20), rgba(255,255,255,0.06))",
                border: "1px solid rgba(255,255,255,0.34)",
                boxShadow: "inset 0 1px 0 rgba(255,255,255,0.38), 0 24px 56px rgba(0,0,0,0.32)",
              }}
            >
              <div style={{ display: "flex", alignItems: "center", gap: 14 }}>
                <AvatarStack size={40} overlap={12} />
                <div style={{ color: "#fff" }}>
                  <div style={{ fontSize: 20, fontWeight: 700, lineHeight: 1 }}>50+</div>
                  <div style={{ fontSize: 13, fontWeight: 500, color: "rgba(255,255,255,0.85)", marginTop: 3 }}>Brands Joined</div>
                </div>
              </div>
              <div style={{ fontSize: 14, lineHeight: 1.6, color: "rgba(255,255,255,0.92)", marginTop: 16 }}>
                Storage, picking, packing, shipping and cash collection — one partner for your entire fulfillment chain.
              </div>
              <Link
                className="btn"
                to="/book?intent=partnership"
                style={{
                  marginTop: 20,
                  padding: "8px 8px 8px 22px",
                  background: "linear-gradient(150deg, rgba(255,255,255,0.2), rgba(255,255,255,0.06))",
                  border: "1px solid rgba(255,255,255,0.4)",
                  color: "#fff",
                  boxShadow: "inset 0 1px 0 rgba(255,255,255,0.35)",
                }}
              >
                Become a Partner
                <span
                  style={{
                    width: 30,
                    height: 30,
                    borderRadius: "50%",
                    background: "#fff",
                    display: "grid",
                    placeItems: "center",
                    color: "#1a1416",
                    marginLeft: 6,
                  }}
                >
                  <ArrowUpRight size={14} />
                </span>
              </Link>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}

function AvatarStack({ size = 40, overlap = 9 }) {
  const faces = [
    { bg: "linear-gradient(160deg,#a3121d,#6b0009)", letter: "Z" },
    { bg: "linear-gradient(160deg,#2a2f3a,#12161d)", letter: "F" },
    { bg: "linear-gradient(160deg,#6b6470,#3a353f)", letter: "N" },
  ];
  return (
    <div style={{ display: "flex" }}>
      {faces.map((f, i) => (
        <div
          key={f.letter}
          style={{
            width: size,
            height: size,
            borderRadius: "50%",
            background: f.bg,
            border: "2px solid rgba(255,255,255,0.9)",
            marginLeft: i ? -overlap : 0,
            display: "grid",
            placeItems: "center",
            color: "#fff",
            fontSize: size > 35 ? 13 : 11,
            fontWeight: 700,
          }}
        >
          {f.letter}
        </div>
      ))}
    </div>
  );
}

function Proof() {
  return (
    <div className="rv" style={{ padding: "60px 20px 30px" }} data-screen-label="Proof">
      <div style={{ maxWidth: 1180, margin: "0 auto" }}>
        <div className="proof-grid" style={{ display: "grid", gridTemplateColumns: "repeat(3, 1fr)", gap: 20, marginBottom: 48 }}>
          <div style={{ textAlign: "center" }}>
            <div className="statnum" data-cnt="200000" data-suf="K+" data-div="1000" style={statNum}>
              0
            </div>
            <div style={statLabel}>Orders Fulfilled</div>
          </div>
          <div style={{ textAlign: "center", borderLeft: "1px solid rgba(26,20,22,0.10)", borderRight: "1px solid rgba(26,20,22,0.10)" }}>
            <div className="statnum" style={statNum}>
              All Egypt
            </div>
            <div style={statLabel}>Nationwide Coverage</div>
          </div>
          <div style={{ textAlign: "center" }}>
            <div className="statnum" data-cnt="50" data-suf="+" style={statNum}>
              0
            </div>
            <div style={statLabel}>Active Brands</div>
          </div>
        </div>
      </div>
      <div style={{ overflow: "hidden" }}>
        <div style={{ textAlign: "center", fontSize: 11.5, letterSpacing: "0.3em", color: "#9c96a0", marginBottom: 26 }}>
          TRUSTED BY BRANDS ACROSS EGYPT
        </div>
        <div className="mq">
          {[...MARQUEE_BRANDS, ...MARQUEE_BRANDS].map((b, i) => (
            <div key={`${b.text}-${i}`} style={b.style}>
              {b.text}
            </div>
          ))}
        </div>
      </div>
    </div>
  );
}

const statNum = {
  fontSize: "clamp(24px, 7vw, 46px)",
  fontWeight: 800,
  color: "#7D010C",
  letterSpacing: "-0.02em",
};
const statLabel = { fontSize: 13.5, color: "#6b6470", marginTop: 4 };

function ServicesSplit() {
  const { fulfillMode, active, setActive } = useLanding();
  if (!fulfillMode) return null;
  const svc = SERVICES[active] || SERVICES[0];
  const aNum = String(active + 1).padStart(2, "0");
  const aProg = `${((active + 1) / SERVICES.length) * 100}%`;
  const stepCount = SERVICES.length;

  const jumpToStep = (i) => {
    setActive(i);
    if (window.matchMedia("(max-width: 880px)").matches) return;
    const track = document.querySelector(".svc-scrub-track");
    if (!track) return;
    const vh = window.innerHeight || 800;
    const travel = Math.max(1, track.offsetHeight - vh);
    const top = track.offsetTop + ((i + 0.5) / stepCount) * travel;
    window.scrollTo({ top, behavior: "smooth" });
  };

  return (
    <div
      id="services"
      className="svc-scrub"
      data-screen-label="Services"
      style={{ ["--svc-steps"]: String(stepCount) }}
    >
      <div className="svc-scrub-intro">
        <div style={{ maxWidth: 1180, margin: "0 auto" }}>
          <div className="rv in" style={{ textAlign: "center", maxWidth: 720, margin: "0 auto" }}>
            <div className="chip" style={{ color: "#7D010C", borderColor: "rgba(125,1,12,0.25)", background: "rgba(125,1,12,0.05)" }}>
              Everything included
            </div>
            <div style={{ fontSize: "clamp(30px, 6.5vw, 44px)", fontWeight: 800, letterSpacing: "-0.01em", marginTop: 18, lineHeight: 1.1 }}>
              One core, every service
            </div>
            <div style={{ fontSize: 16, color: "#6b6470", marginTop: 14 }}>
              Ten operations built around one system — scroll to explore each.
            </div>
          </div>
        </div>
      </div>

      <div className="svc-scrub-track">
        <div className="svc-scrub-sticky">
          <div style={{ maxWidth: 1180, margin: "0 auto", width: "100%", height: "100%" }}>
            <div className="svc-grid" style={{ display: "grid", gridTemplateColumns: "0.92fr 1fr", gap: 40, alignItems: "center", height: "100%" }}>
              <div className="svc-sticky">
                <div
                  style={{
                    position: "relative",
                    borderRadius: 30,
                    overflow: "hidden",
                    background: "linear-gradient(160deg, #1c2429, #0e1417)",
                    color: "#fff",
                    padding: "36px 36px 80px",
                    minHeight: 420,
                    height: "min(520px, calc(100vh - 140px))",
                    boxShadow: "0 34px 74px rgba(18,26,30,0.34)",
                  }}
                >
                  <div
                    style={{
                      position: "absolute",
                      top: -46,
                      right: 2,
                      fontSize: 200,
                      fontWeight: 800,
                      color: "rgba(255,255,255,0.045)",
                      lineHeight: 1,
                      pointerEvents: "none",
                    }}
                  >
                    {aNum}
                  </div>
                  <div className="ico" style={{ width: 56, height: 56, borderRadius: 17 }}>
                    <Icon paths={svc.icon} size={22} />
                  </div>
                  <div style={{ fontSize: "clamp(22px, 4vw, 30px)", fontWeight: 800, marginTop: 22, letterSpacing: "-0.01em" }}>{svc.t}</div>
                  <div style={{ fontSize: 15, lineHeight: 1.7, color: "rgba(255,255,255,0.78)", marginTop: 14, maxWidth: 380 }}>
                    {svc.d}
                  </div>
                  <div style={{ position: "absolute", left: 36, right: 36, bottom: 28 }}>
                    <div
                      style={{
                        display: "flex",
                        justifyContent: "space-between",
                        fontSize: 12,
                        color: "rgba(255,255,255,0.55)",
                        marginBottom: 11,
                        letterSpacing: "0.04em",
                      }}
                    >
                      <span>
                        Service {active + 1} of {stepCount}
                      </span>
                      <span style={{ letterSpacing: "0.18em", fontWeight: 700 }}>CHAINZ</span>
                    </div>
                    <div style={{ height: 4, borderRadius: 999, background: "rgba(255,255,255,0.12)", overflow: "hidden" }}>
                      <div
                        style={{
                          height: "100%",
                          borderRadius: 999,
                          background: "linear-gradient(90deg, #a3121d, #e0424f)",
                          width: aProg,
                          transition: "width .45s cubic-bezier(.22,.8,.3,1)",
                        }}
                      />
                    </div>
                  </div>
                </div>
              </div>

              <div className="svc-list" style={{ display: "flex", flexDirection: "column", maxHeight: "calc(100vh - 120px)", overflow: "hidden" }}>
                {SERVICES.map((s, i) => {
                  const on = i === active;
                  return (
                    <div
                      key={s.t}
                      className="svc-item"
                      data-svc-index={i}
                      onClick={() => jumpToStep(i)}
                      role="button"
                      tabIndex={0}
                      onKeyDown={(e) => e.key === "Enter" && jumpToStep(i)}
                      style={{
                        display: "flex",
                        gap: 16,
                        padding: "12px 4px",
                        borderTop: "1px solid rgba(26,20,22,0.09)",
                        cursor: "pointer",
                        opacity: on ? 1 : 0.42,
                        transition: "opacity .3s ease",
                        flexShrink: 0,
                      }}
                    >
                      <div
                        className="svc-bar"
                        style={{
                          width: 3,
                          flexShrink: 0,
                          borderRadius: 999,
                          background: on ? "#a3121d" : "transparent",
                          transition: "background .3s ease",
                        }}
                      />
                      <div
                        style={{
                          fontSize: 13,
                          fontWeight: 800,
                          color: on ? "#7D010C" : "#b3adb5",
                          minWidth: 28,
                          transition: "color .3s ease",
                        }}
                      >
                        {String(i + 1).padStart(2, "0")}
                      </div>
                      <div style={{ minWidth: 0 }}>
                        <div style={{ fontSize: 17, fontWeight: 700, color: on ? "#17222a" : "#3a343e", transition: "color .3s ease" }}>
                          {s.t}
                        </div>
                        <div
                          className="svc-desc"
                          style={{
                            fontSize: 13,
                            lineHeight: 1.5,
                            color: "#6b6470",
                            marginTop: on ? 4 : 0,
                            maxHeight: on ? 60 : 0,
                            opacity: on ? 1 : 0,
                            overflow: "hidden",
                            transition: "max-height .35s ease, opacity .25s ease, margin .25s ease",
                          }}
                        >
                          {s.d}
                        </div>
                      </div>
                    </div>
                  );
                })}
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}

function SupplyChain() {
  const { activeSupply, setActiveSupply } = useLanding();

  return (
    <div style={{ padding: "90px 20px" }} data-screen-label="SupplyChain">
      <div style={{ maxWidth: 1120, margin: "0 auto" }}>
        <div className="rv" style={{ textAlign: "center", maxWidth: 640, margin: "0 auto 40px" }}>
          <div className="chip" style={{ color: "#7D010C", borderColor: "rgba(125,1,12,0.25)", background: "rgba(125,1,12,0.05)" }}>
            End-to-end visibility
          </div>
          <div style={{ fontSize: "clamp(28px, 6vw, 40px)", fontWeight: 800, letterSpacing: "-0.01em", marginTop: 16, lineHeight: 1.1 }}>
            One connected supply chain, start to finish
          </div>
          <div style={{ fontSize: 16, color: "#6b6470", marginTop: 14 }}>Tap a stage to see exactly what happens there.</div>
        </div>
        <div className="rv" style={{ position: "relative", maxWidth: 620, margin: "0 auto" }}>
          <img
            src={assets.supplyChain}
            alt="Chainz supply chain, from manufacturing to delivery"
            onError={onAssetError}
            style={{ width: "100%", height: "auto", display: "block" }}
          />
          {SUPPLY_STAGES.map((st) => {
            const active = activeSupply === st.id;
            const tx = st.align === "left" ? "0%" : st.align === "right" ? "-100%" : "-50%";
            const ty = st.pop === "above" ? "calc(-100% - 24px)" : "24px";
            return (
              <React.Fragment key={st.id}>
                <button
                  type="button"
                  className="supply-dot"
                  onClick={() => setActiveSupply(active ? null : st.id)}
                  style={{
                    left: `${st.left}%`,
                    top: `${st.top}%`,
                    background: active ? "#7D010C" : "#fff",
                    color: active ? "#fff" : "#7D010C",
                  }}
                  aria-label={st.name}
                >
                  {st.num}
                </button>
                {active && (
                  <div
                    className="supply-pop"
                    style={{
                      left: `${st.left}%`,
                      top: `${st.top}%`,
                      transform: `translate(${tx}, ${ty})`,
                    }}
                  >
                    <div style={{ display: "flex", alignItems: "baseline", gap: 8, marginBottom: 8 }}>
                      <div style={{ fontSize: 11, fontWeight: 800, color: "#7D010C", letterSpacing: "0.06em" }}>{st.num}</div>
                      <div style={{ fontSize: 15, fontWeight: 800, color: "#1a1416" }}>{st.name}</div>
                    </div>
                    {st.lines.map((ln) => (
                      <div
                        key={ln}
                        style={{
                          display: "flex",
                          alignItems: "flex-start",
                          gap: 7,
                          fontSize: 12.5,
                          lineHeight: 1.5,
                          color: "#4a444e",
                          marginTop: 5,
                        }}
                      >
                        <div
                          style={{
                            width: 5,
                            height: 5,
                            borderRadius: "50%",
                            background: "#c0303e",
                            marginTop: 6,
                            flexShrink: 0,
                          }}
                        />
                        {ln}
                      </div>
                    ))}
                  </div>
                )}
              </React.Fragment>
            );
          })}
        </div>
      </div>
    </div>
  );
}

function ProductsBlock() {
  const { productsMode, goTo } = useLanding();
  if (!productsMode) return null;

  return (
    <div id="products" style={{ padding: "116px 20px 90px" }} data-screen-label="Products">
      <div style={{ maxWidth: 1180, margin: "0 auto" }}>
        <div className="rv" style={{ textAlign: "center", maxWidth: 740, margin: "0 auto 54px" }}>
          <div className="chip" style={{ color: "#7D010C", borderColor: "rgba(125,1,12,0.25)", background: "rgba(125,1,12,0.05)" }}>
            The Chainz Suite
          </div>
          <div style={{ fontSize: "clamp(30px, 6.5vw, 44px)", fontWeight: 800, letterSpacing: "-0.01em", marginTop: 18, lineHeight: 1.1 }}>
            Coming Soon
          </div>
          <div style={{ fontSize: 16, color: "#6b6470", marginTop: 14 }}>
            The same systems that power Chainz — licensed for brands operating their own warehouse and team. We're putting the finishing
            touches on.
          </div>
        </div>

        <div
          className="rv coming-soon-hero"
          style={{
            position: "relative",
            borderRadius: 34,
            overflow: "hidden",
            minHeight: 320,
            background: "linear-gradient(150deg, #14100f, #050506)",
            border: "1px solid rgba(255,255,255,0.08)",
            boxShadow: "0 30px 70px rgba(0,0,0,0.28), inset 0 1px 0 rgba(255,255,255,0.06)",
            marginBottom: 28,
          }}
        >
          <div
            style={{
              position: "absolute",
              inset: 0,
              background: "radial-gradient(700px 340px at 85% 0%, rgba(125,1,12,0.4), transparent 62%)",
              pointerEvents: "none",
            }}
          />
          <div
            style={{
              position: "relative",
              zIndex: 2,
              padding: "56px clamp(24px, 5vw, 56px)",
              display: "flex",
              flexDirection: "column",
              alignItems: "flex-start",
              maxWidth: 680,
            }}
          >
            <div
              className="chip"
              style={{
                color: "#fff",
                borderColor: "rgba(255,255,255,0.22)",
                background: "rgba(255,255,255,0.08)",
                gap: 10,
              }}
            >
              <span
                className="ico"
                style={{
                  width: 28,
                  height: 28,
                  borderRadius: 8,
                  background: "rgba(255,255,255,0.1)",
                  borderColor: "rgba(255,255,255,0.18)",
                  color: "#fff",
                }}
              >
                <Icon paths={iconPaths.clock} size={14} />
              </span>
              Launching soon
            </div>
            <div
              style={{
                fontSize: "clamp(26px, 5vw, 38px)",
                fontWeight: 800,
                color: "#fff",
                lineHeight: 1.1,
                letterSpacing: "-0.02em",
                marginTop: 22,
              }}
            >
              Software that runs like Chainz — for your own warehouse
            </div>
            <div style={{ fontSize: 16, color: "rgba(255,255,255,0.82)", marginTop: 16, lineHeight: 1.65 }}>
              EcomHQ, Backtracking, POS Bazaars and self-fulfillment are on the way. Join the waitlist and we'll reach out when they're
              ready.
            </div>
            <Link className="btn red" to="/contact" onClick={goTo("contact-page", null, "contact")} style={{ marginTop: 28, padding: "13px 26px" }}>
              Get in touch
              <ArrowRight />
            </Link>
          </div>
        </div>

        <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(262px, 1fr))", gap: 18 }}>
          {PRODUCTS.map((p) => (
            <div key={p.name} className="card svc rv" style={{ padding: 28, display: "flex", flexDirection: "column" }}>
              <div style={{ display: "flex", alignItems: "flex-start", justifyContent: "space-between", gap: 12 }}>
                <div className="ico">
                  <Icon paths={p.icon} />
                </div>
                <div className="chip" style={{ color: "#7D010C", borderColor: "rgba(125,1,12,0.25)", background: "rgba(125,1,12,0.05)" }}>
                  Coming soon
                </div>
              </div>
              <div style={{ fontSize: 19, fontWeight: 800, margin: "16px 0 4px" }}>{p.name}</div>
              <div style={{ fontSize: 13, fontWeight: 600, color: "#7D010C" }}>{p.tagline}</div>
              <div style={{ fontSize: 13.5, lineHeight: 1.6, color: "#6b6470", marginTop: 12 }}>{p.desc}</div>
              <div style={{ display: "flex", flexDirection: "column", gap: 9, marginTop: 16, flex: 1 }}>
                {p.features.slice(0, 3).map((f) => (
                  <div key={f} className="pfeat" style={{ display: "flex", alignItems: "center", gap: 9, fontSize: 13, color: "#2a242e" }}>
                    <Check />
                    {f}
                  </div>
                ))}
              </div>
            </div>
          ))}
        </div>
      </div>
    </div>
  );
}

function HowItWorks() {
  const { fulfillMode, howPlaying, setHowPlaying } = useLanding();
  if (!fulfillMode) return null;

  return (
    <div id="how" style={{ padding: "90px 20px" }} data-screen-label="HowItWorks">
      <div style={{ maxWidth: 1120, margin: "0 auto" }}>
        <div className="rv" style={{ textAlign: "center", maxWidth: 640, margin: "0 auto 32px" }}>
          <div className="chip" style={{ color: "#7D010C", borderColor: "rgba(125,1,12,0.25)", background: "rgba(125,1,12,0.05)" }}>
            How it works
          </div>
          <div style={{ fontSize: "clamp(28px, 6vw, 40px)", fontWeight: 800, letterSpacing: "-0.01em", marginTop: 16, lineHeight: 1.1 }}>
            Follow one order through Chainz
          </div>
        </div>
        <div
          className="rv"
          style={{
            position: "relative",
            borderRadius: 26,
            overflow: "hidden",
            aspectRatio: "16 / 9",
            background: "#14090b",
            boxShadow: "0 30px 64px rgba(50,25,30,0.14)",
            border: "1px solid rgba(26,20,22,0.08)",
          }}
        >
          {!howPlaying ? (
            <>
              <img
                src={assets.howPoster}
                alt="Follow one order through Chainz"
                onError={onAssetError}
                style={{ position: "absolute", inset: 0, width: "100%", height: "100%", objectFit: "cover" }}
              />
              <div
                style={{
                  position: "absolute",
                  inset: 0,
                  background: "linear-gradient(180deg, rgba(12,6,7,0.1) 0%, rgba(12,6,7,0.55) 100%)",
                }}
              />
              <button
                type="button"
                onClick={() => setHowPlaying(true)}
                aria-label="Play how it works video"
                style={{
                  position: "absolute",
                  inset: 0,
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                  cursor: "pointer",
                  background: "transparent",
                  border: "none",
                }}
              >
                <div
                  style={{
                    width: 78,
                    height: 78,
                    borderRadius: "50%",
                    background: "#fff",
                    border: "1px solid rgba(26,20,22,0.12)",
                    display: "grid",
                    placeItems: "center",
                    boxShadow: "0 10px 24px rgba(50,25,30,0.25)",
                  }}
                >
                  <Play />
                </div>
              </button>
            </>
          ) : (
            <video className="how-video" controls autoPlay playsInline style={{ position: "absolute", inset: 0, width: "100%", height: "100%", objectFit: "cover", background: "#000" }}>
              <source src={assets.howVideo} type="video/mp4" />
              <source src="https://chainz-landing-page.vercel.app/uploads/videos/how-it-works.mp4" type="video/mp4" />
            </video>
          )}
        </div>
        <div className="rv" style={{ display: "flex", flexWrap: "wrap", justifyContent: "center", gap: 10, marginTop: 28 }}>
          {STAGE_LABELS.map((label, i) => (
            <div key={label} className="chip" title={STEPS[i]?.d}>
              {label}
            </div>
          ))}
        </div>
      </div>
    </div>
  );
}

function Categories() {
  return (
    <div style={{ padding: "90px 20px" }} data-screen-label="Categories">
      <div style={{ maxWidth: 1180, margin: "0 auto" }}>
        <div className="rv cat-header" style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 56, alignItems: "center", marginBottom: 56 }}>
          <div>
            <div className="chip" style={{ color: "#7D010C", borderColor: "rgba(125,1,12,0.25)", background: "rgba(125,1,12,0.05)" }}>
              Any product, any category
            </div>
            <div style={{ fontSize: "clamp(30px, 6.5vw, 44px)", fontWeight: 800, letterSpacing: "-0.01em", marginTop: 18, lineHeight: 1.1 }}>
              Fulfillment built for every product you sell
            </div>
            <div style={{ fontSize: 16, color: "#6b6470", marginTop: 14, lineHeight: 1.6 }}>
              From skincare to electronics, books to home decor — our warehouse, systems and couriers flex to whatever you sell, with the same QC'd,
              barcode-tracked care for every unit.
            </div>
          </div>
          <div
            style={{
              position: "relative",
              borderRadius: 26,
              overflow: "hidden",
              aspectRatio: "4 / 3",
              boxShadow: "0 30px 64px rgba(50,25,30,0.14)",
              border: "1px solid rgba(26,20,22,0.08)",
            }}
          >
            <video
              className="cat-box-video"
              autoPlay
              muted
              loop
              playsInline
              preload="auto"
              style={{ position: "absolute", inset: 0, width: "100%", height: "100%", objectFit: "cover" }}
            >
              <source src={assets.categoriesVideo} type="video/mp4" />
              <source src="https://chainz-landing-page.vercel.app/uploads/videos/categories-box.mp4" type="video/mp4" />
            </video>
          </div>
        </div>
        <div className="cat-grid" style={{ display: "grid", gridTemplateColumns: "repeat(4, 1fr)", gap: 16 }}>
          {CATEGORIES.map((c) => (
            <div key={c.name} className="card cat-tile rv" style={{ padding: 22, display: "flex", flexDirection: "column", alignItems: "flex-start", gap: 14 }}>
              <div className="ico">
                <Icon paths={c.icon} />
              </div>
              <div style={{ fontSize: 14.5, fontWeight: 700 }}>{c.name}</div>
            </div>
          ))}
        </div>
      </div>
    </div>
  );
}

function Integrations() {
  return (
    <div id="integrations" style={{ padding: "90px 20px" }} data-screen-label="Integrations">
      <div style={{ maxWidth: 1180, margin: "0 auto" }}>
        <div className="rv card" style={{ padding: "50px 40px", textAlign: "center" }}>
          <div className="chip" style={{ color: "#7D010C", borderColor: "rgba(125,1,12,0.25)", background: "rgba(125,1,12,0.05)" }}>
            Plug & play
          </div>
          <div style={{ fontSize: "clamp(28px, 6vw, 40px)", fontWeight: 800, letterSpacing: "-0.01em", marginTop: 16 }}>
            Connects to the tools you already run
          </div>
          <div style={{ fontSize: 16, color: "#6b6470", marginTop: 12, maxWidth: 560, marginLeft: "auto", marginRight: "auto" }}>
            Sync your store, couriers and confirmation flow in minutes — no engineering required.
          </div>
          <div style={{ display: "flex", flexWrap: "wrap", justifyContent: "center", alignItems: "center", gap: 56, marginTop: 44 }}>
            {INTEGRATIONS.map((i) => (
              <div key={i.name} className="intlogo" style={{ display: "flex", flexDirection: "column", alignItems: "center", gap: 12 }}>
                <img src={i.img} alt={i.name} onError={onAssetError} style={{ height: 48, width: "auto", objectFit: "contain" }} />
                <div style={{ fontSize: 13.5, fontWeight: 600, color: "#6b6470" }}>{i.name}</div>
              </div>
            ))}
          </div>
        </div>
      </div>
    </div>
  );
}

function TestimonialQuote({ quote, featured }) {
  const paragraphs = Array.isArray(quote) ? quote : [quote];
  return (
    <div
      className="tquote"
      style={{
        fontSize: featured ? 16 : 15,
        lineHeight: 1.7,
        color: "#2a242e",
        margin: "16px 0 22px",
        textWrap: "pretty",
      }}
    >
      {paragraphs.map((p, i) => {
        const first = i === 0;
        const last = i === paragraphs.length - 1;
        return (
          <p key={p.slice(0, 24)} style={{ margin: first ? 0 : "14px 0 0" }}>
            {first ? `“${p}` : p}
            {last ? "”" : ""}
          </p>
        );
      })}
    </div>
  );
}

function Testimonials() {
  return (
    <div style={{ padding: "20px 20px 90px" }} data-screen-label="Testimonials">
      <div style={{ maxWidth: 1180, margin: "0 auto" }}>
        <div className="rv" style={{ textAlign: "center", maxWidth: 700, margin: "0 auto 48px" }}>
          <div className="chip" style={{ color: "#7D010C", borderColor: "rgba(125,1,12,0.25)", background: "rgba(125,1,12,0.05)" }}>
            Loved by founders
          </div>
          <div style={{ fontSize: "clamp(30px, 6.5vw, 44px)", fontWeight: 800, letterSpacing: "-0.01em", marginTop: 18, lineHeight: 1.1 }}>
            Brands that stopped worrying about ops
          </div>
        </div>
        <div className="tgrid">
          {TESTIMONIALS.map((t) => (
            <div key={t.name} className={`card rv${t.featured ? " tcard-featured" : ""}`} style={{ padding: t.featured ? 36 : 28 }}>
              <div style={{ color: "#e0a83c", fontSize: 15, letterSpacing: 2 }}>★★★★★</div>
              <TestimonialQuote quote={t.quote} featured={t.featured} />
              <div style={{ display: "flex", alignItems: "center", gap: 12 }}>
                <div
                  style={{
                    width: 44,
                    height: 44,
                    borderRadius: "50%",
                    background: t.bg,
                    display: "grid",
                    placeItems: "center",
                    color: "#fff",
                    fontWeight: 700,
                    fontSize: 15,
                  }}
                >
                  {t.initial}
                </div>
                <div>
                  <div style={{ fontSize: 14, fontWeight: 700 }}>{t.name}</div>
                  <div style={{ fontSize: 12.5, color: "#6b6470" }}>{t.role}</div>
                </div>
              </div>
            </div>
          ))}
        </div>
      </div>
    </div>
  );
}

function FAQ() {
  const { faqOpen, setFaqOpen } = useLanding();
  return (
    <div id="faq" style={{ padding: "20px 20px 90px" }} data-screen-label="FAQ">
      <div style={{ maxWidth: 860, margin: "0 auto" }}>
        <div className="rv" style={{ textAlign: "center", marginBottom: 44 }}>
          <div style={{ fontSize: "clamp(30px, 6.5vw, 44px)", fontWeight: 800, letterSpacing: "-0.01em" }}>Questions, answered</div>
        </div>
        <div style={{ display: "flex", flexDirection: "column", gap: 12 }}>
          {FAQS.map(([q, a], i) => {
            const open = faqOpen === i;
            return (
              <div
                key={q}
                className="card rv"
                onClick={() => setFaqOpen(open ? -1 : i)}
                role="button"
                tabIndex={0}
                onKeyDown={(e) => e.key === "Enter" && setFaqOpen(open ? -1 : i)}
                style={{
                  padding: "22px 26px",
                  cursor: "pointer",
                  borderColor: open ? "rgba(125,1,12,0.3)" : "rgba(26,20,22,0.07)",
                }}
              >
                <div style={{ display: "flex", alignItems: "center", gap: 12 }}>
                  <div style={{ fontSize: 16, fontWeight: 700 }}>{q}</div>
                  <div
                    style={{
                      marginLeft: "auto",
                      width: 26,
                      height: 26,
                      flexShrink: 0,
                      borderRadius: 9,
                      display: "grid",
                      placeItems: "center",
                      background: "rgba(125,1,12,0.08)",
                      border: "1px solid rgba(125,1,12,0.2)",
                      color: "#7D010C",
                      fontWeight: 700,
                      fontSize: 15,
                    }}
                  >
                    {open ? "−" : "+"}
                  </div>
                </div>
                {open && (
                  <div style={{ fontSize: 14, lineHeight: 1.7, color: "#6b6470", marginTop: 14, textWrap: "pretty" }}>{a}</div>
                )}
              </div>
            );
          })}
        </div>
      </div>
    </div>
  );
}

function FinalCta() {
  const { goTo } = useLanding();
  return (
    <div id="cta" style={{ padding: "20px 20px 90px" }} data-screen-label="CTA">
      <div
        className="rv"
        style={{
          maxWidth: 1180,
          margin: "0 auto",
          position: "relative",
          borderRadius: 34,
          overflow: "hidden",
          minHeight: 460,
          background: "linear-gradient(150deg, #14100f, #050506)",
          border: "1px solid rgba(255,255,255,0.08)",
          boxShadow: "0 30px 70px rgba(0,0,0,0.5), inset 0 1px 0 rgba(255,255,255,0.06)",
        }}
      >
        <div
          style={{
            position: "absolute",
            inset: 0,
            background: "radial-gradient(700px 340px at 85% 0%, rgba(125,1,12,0.4), transparent 62%)",
            pointerEvents: "none",
          }}
        />
        <div style={{ position: "relative", zIndex: 2, padding: "76px 50px", maxWidth: 620 }}>
          <div style={{ fontSize: "clamp(32px, 7vw, 48px)", fontWeight: 800, color: "#fff", lineHeight: 1.06, letterSpacing: "-0.02em" }}>
            Ready to hand it over?
          </div>
          <div style={{ fontSize: 17, color: "rgba(255,255,255,0.88)", marginTop: 16, lineHeight: 1.6 }}>
            Join 50+ brands running their fulfillment on Chainz. Hand over operations and stay focused on growing the brand.
          </div>
          <div style={{ display: "flex", gap: 13, marginTop: 30, flexWrap: "wrap" }}>
            <Link className="btn red" to="/book?intent=partnership" style={{ padding: "15px 30px", fontSize: 15 }}>
              Become a Partner
              <ArrowRight />
            </Link>
            <Link className="btn ghost-lite" to="/book?intent=demo" style={{ padding: "15px 30px", fontSize: 15 }}>
              Book a Demo
            </Link>
          </div>
        </div>
      </div>
    </div>
  );
}
